import { api } from '../api';
import { Bars, CalibrationChart, LineChart } from '../components/Charts';
import { Bar, Empty, ErrorBox, Loading, Metric, PageHead, Panel } from '../components/ui';
import { fixed, pct, pending, shortDate, signedPct, signedRel } from '../format';
import { useApi } from '../hooks';
import { KIND } from '../labels';
import { DRILL_META, FACTORS } from '../drills/meta';
import type { CalibStats, DecisionStats, GroupRate, MemoryStats, Proportion, TrainingStats } from '../types';
import { verdictText } from './Decisions';

const ci = (p: Proportion) => (p.lo == null || p.hi == null ? '—' : `${pct(p.lo)}~${pct(p.hi)}`);

export function Stats() {
  const mem = useApi(api.memoryStats);
  const dec = useApi(api.decisionStats);
  const train = useApi(api.trainingStats);
  return (
    <>
      <PageHead title="성장 분석" sub="합산 점수는 없습니다. 능력마다 따로, 표본 수(n)와 95% 신뢰구간을 같이 봅니다." />
      <h2 className="section-title">두뇌 훈련</h2>
      {train.error ? <ErrorBox message={train.error} onRetry={train.reload} /> : !train.data ? <Loading /> : <TrainingSection t={train.data} />}
      <h2 className="section-title">기억</h2>
      {mem.error ? <ErrorBox message={mem.error} onRetry={mem.reload} /> : !mem.data ? <Loading /> : <MemorySection m={mem.data} />}
      <h2 className="section-title">판단</h2>
      {dec.error ? <ErrorBox message={dec.error} onRetry={dec.reload} /> : !dec.data ? <Loading /> : <DecisionSection s={dec.data} />}
    </>
  );
}

function TrainingSection({ t }: { t: TrainingStats }) {
  if (t.days_trained === 0) {
    return (
      <Panel>
        <Empty>아직 훈련 기록이 없습니다. <a href="#/train">오늘 훈련</a>을 하면 종목별 레벨 변화가 여기에 쌓입니다.</Empty>
      </Panel>
    );
  }
  const tried = t.drills.filter((d) => d.sessions > 0);
  const untried = t.drills.filter((d) => d.sessions === 0);
  return (
    <>
      {FACTORS.map((f) => {
        const group = tried.filter((d) => DRILL_META[d.key].factor === f.key);
        if (!group.length) return null;
        return (
          <section key={f.key} className="factor-stats">
            <h3 className="factor-title">{f.label}</h3>
            <div className="train-stats">{group.map((d) => <DrillPanel key={d.key} d={d} t={t} />)}</div>
          </section>
        );
      })}
      {untried.length > 0 && (
        <p className="small muted">아직 안 한 종목: {untried.map((d, i) => <span key={d.key}>{i > 0 && ', '}<a className="link" href={`#/train/${d.key}`}>{DRILL_META[d.key].label}</a></span>)}</p>
      )}
    </>
  );
}

function DrillPanel({ d, t }: { d: TrainingStats['drills'][number]; t: TrainingStats }) {
  return (
        <Panel title={DRILL_META[d.key].label} action={<a className="link small" href={`#/train/${d.key}`}>한 판 하기</a>}>
          <div className="metrics-2">
            <Metric label="지금 레벨" value={`Lv ${d.level}`} sub={d.first_level ? `시작 Lv ${d.first_level} · 최고 통과 Lv ${d.best_cleared}` : '아직 안 함'} />
            <Metric label="최근 5판 정답률" value={pct(d.recent_accuracy)} sub={`${d.sessions}판 · ${d.days}일`} />
          </div>
          {d.history.length >= 2 ? (
            <LineChart
              points={d.history.map((h) => ({ label: shortDate(h.day), value: h.level }))}
              min={1} max={10} height={120} format={(v) => `Lv ${Math.round(v)}`} ariaLabel={`${DRILL_META[d.key].label} 날짜별 레벨`}
            />
          ) : (
            <Empty>이틀 이상 하면 날짜별 레벨 그래프가 그려집니다.</Empty>
          )}
          {d.calibration && <CalibBlock c={d.calibration} />}
          {d.days > 0 && d.days < t.min_trend_days && (
            <p className="small muted">{pending(d.days, t.min_trend_days, '일')} — 레벨이 정말 오르는지는 1주 이상 쌓여야 말할 수 있습니다.</p>
          )}
        </Panel>
  );
}

function calibNote(c: CalibStats): string {
  const parts: string[] = [];
  if (c.overconfidence && c.n >= c.min_n) parts.push(verdictText(c.overconfidence, c.min_n));
  if (c.interval.verdict === 'narrow') parts.push('90% 구간이 너무 좁습니다 — 더 넓게 잡으세요.');
  else if (c.interval.verdict === 'wide') parts.push('90% 구간이 필요 이상 넓습니다 — 조금 좁혀도 됩니다.');
  else if (c.interval.verdict === 'ok') parts.push('90% 구간이 잘 맞습니다.');
  return parts.length ? parts.join(' ') : '문제가 쌓이면 확신과 실제가 맞는지 판정합니다. 이 종목의 레벨은 비교하는 두 값이 얼마나 가까운지(어려움)입니다.';
}

/** 확신도 맞히기 누적 보정 — 레벨보다 이것이 이 종목의 진짜 성적 */
function CalibBlock({ c }: { c: CalibStats }) {
  const oc = c.overconfidence;
  const iv = c.interval;
  return (
    <>
      <div className="metrics-2">
        <Metric
          label="과신 (확신 − 정답률)" value={oc ? signedPct(oc.gap) : '—'}
          sub={oc ? `확신 ${pct(oc.mean_conf)} · 정답 ${pct(oc.hit_rate)}` : '50%가 아닌 확신이 쌓이면 계산'}
          insufficient={c.n < c.min_n ? pending(c.n, c.min_n, '문제') : undefined}
        />
        <Metric
          label="90% 구간 적중" value={pct(iv.rate)} sub={`목표 90% · ${iv.hits}/${iv.n}`}
          insufficient={iv.n < iv.min_n ? pending(iv.n, iv.min_n, '문제') : undefined}
        />
      </div>
      <p className="small muted calib-stats-note">{calibNote(c)}</p>
    </>
  );
}

function RateTable({ rows, target, empty }: { rows: GroupRate[]; target: number; empty: string }) {
  if (!rows.length) return <Empty>{empty}</Empty>;
  return (
    <table className="table">
      <thead>
        <tr><th>구분</th><th>유지율</th><th>95% 구간</th><th>n</th></tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.key} className={r.n < 10 ? 'dim' : r.rate != null && r.rate < target - 0.05 ? 'bad-row' : ''}>
            <td>{r.label}</td>
            <td className="num">{pct(r.rate)}</td>
            <td className="num muted">{ci(r)}</td>
            <td className="num">{r.n}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function pvaText(m: MemoryStats): string {
  const { predicted, actual, n } = m.predicted_vs_actual;
  if (n < 30 || predicted == null || actual == null) return `복습 단계 기록이 30회 이상 쌓이면 비교합니다 (지금 ${n}회).`;
  if (actual < predicted - 0.05) return '실제가 예측보다 낮습니다. 기본 모델보다 빨리 잊는 편입니다 — 복습 1,000회쯤 쌓이면 매개변수 개인화를 검토합니다.';
  if (actual > predicted + 0.05) return '실제가 예측보다 높습니다. 간격을 더 늘려도 됩니다 (설정의 목표 유지율을 조금 낮추면 복습이 줄어듭니다).';
  return '예측과 실제가 비슷합니다 — 복습 일정이 잘 맞고 있습니다.';
}

function MemorySection({ m }: { m: MemoryStats }) {
  const r = m.retention;
  const insufficient = (p: Proportion) => (p.n < m.min_n ? pending(p.n, m.min_n, '회') : undefined);
  const maxErr = Math.max(1, ...m.errors.map((e) => e.count));
  return (
    <div className="dash">
      <Panel title="기억 유지율" className="span-2">
        <div className="metrics-row">
          <Metric label="7일" value={pct(r.d7.rate)} sub={`${ci(r.d7)} · n=${r.d7.n}`} insufficient={insufficient(r.d7)} />
          <Metric label="30일" value={pct(r.d30.rate)} sub={`${ci(r.d30)} · n=${r.d30.n}`} insufficient={insufficient(r.d30)} />
          <Metric label="90일" value={pct(r.d90.rate)} sub={`${ci(r.d90)} · n=${r.d90.n}`} insufficient={insufficient(r.d90)} />
          <Metric label="전체" value={pct(r.all.rate)} sub={`${ci(r.all)} · n=${r.all.n}`} insufficient={insufficient(r.all)} />
        </div>
        {m.weekly.some((w) => w.n >= 5) ? (
          <LineChart points={m.weekly.map((w) => ({ label: shortDate(w.week), value: w.rate, n: w.n }))} min={0.5} max={1} target={m.target} format={(v) => pct(v)} minN={5} ariaLabel="주별 유지율 12주" />
        ) : (
          <Empty>복습 단계 기록이 주 5회 이상 쌓이면 주별 추세를 그립니다.</Empty>
        )}
        <p className="small muted">복습 단계 카드에서 '다시'가 아닌 비율. 처음 회상과 10분 재확인은 뺍니다. 점선 = 목표 {pct(m.target)}.</p>
      </Panel>
      <Panel title="FSRS 예측 vs 실제">
        <div className="metrics-2">
          <Metric label="예측한 기억 확률" value={pct(m.predicted_vs_actual.predicted)} sub={`n=${m.predicted_vs_actual.n}`} />
          <Metric label="실제로 떠올린 비율" value={pct(m.predicted_vs_actual.actual)} />
        </div>
        <p className="small muted">{pvaText(m)}</p>
      </Panel>
      <Panel title="카드 종류별 (90일)">
        <RateTable rows={m.by_kind} target={m.target} empty="아직 복습 단계 기록이 없습니다." />
        <p className="small muted">적용·원리 카드가 정의 카드보다 많이 낮으면 '외웠지만 이해는 얕다'는 신호입니다.</p>
      </Panel>
      <Panel title="분야별 (90일)"><RateTable rows={m.by_domain} target={m.target} empty="아직 복습 단계 기록이 없습니다." /></Panel>
      <Panel title="간격별 (90일)"><RateTable rows={m.by_interval} target={m.target} empty="아직 복습 단계 기록이 없습니다." /></Panel>
      <Panel title="실수 유형 (90일)">
        {m.errors.length === 0 ? (
          <Empty>'다시'를 고를 때 실수 유형을 남기면 여기 쌓입니다.</Empty>
        ) : (
          <ul className="list">
            {m.errors.map((e) => (
              <li key={e.type} className="list-row err-row">
                <span className="grow">{e.label}<Bar value={e.count / maxErr} tone="warn" /></span>
                <b>{e.count}</b>
              </li>
            ))}
          </ul>
        )}
      </Panel>
      <Panel title="반복해서 잊는 개념">
        {m.leeches.length === 0 ? (
          <Empty>4번 이상 잊은 카드가 없습니다.</Empty>
        ) : (
          <ul className="list">
            {m.leeches.map((l) => (
              <li key={l.card_id}>
                <a className="list-row" href={`#/knowledge/${l.item_id}`}>
                  <span className="grow"><b>{l.title}</b><span className="muted small">{KIND[l.kind]} 카드</span></span>
                  <span className="badge bad">{l.lapses}번 잊음</span>
                </a>
              </li>
            ))}
          </ul>
        )}
      </Panel>
      <Panel title="복습 예정 (14일)">
        <Bars data={m.forecast.map((f) => ({ label: shortDate(f.day), value: f.count }))} ariaLabel="앞으로 14일 복습 예정" />
      </Panel>
      <Panel title="기록">
        <div className="metrics-2">
          <Metric label="총 복습" value={m.totals.reviews} sub={`${m.totals.days}일 · ${m.totals.minutes}분`} />
          <Metric label="답을 쓰고 채점한 비율" value={pct(m.typed_ratio.rate)} sub="자기 채점의 정직성 보조 지표 (90일)" />
        </div>
      </Panel>
    </div>
  );
}

function DecisionSection({ s }: { s: DecisionStats }) {
  const b = s.brier;
  const oc = s.overconfidence;
  const q = s.quality;
  const few = (n: number) => (n < s.min_n ? pending(n, s.min_n, '건') : undefined);
  return (
    <div className="dash">
      <Panel title="브라이어 점수" className="span-2">
        <div className="metrics-row">
          <Metric label="전체" value={fixed(b.all.value)} sub={`n=${b.all.n}`} insufficient={few(b.all.n)} />
          <Metric label="최근 90일" value={fixed(b.d90.value)} sub={`n=${b.d90.n}`} insufficient={few(b.d90.n)} />
          <Metric
            label="늘 50%라고 말한 경우 대비"
            value={b.skill_vs_coin == null ? '—' : signedRel(b.skill_vs_coin)}
            sub="+ 면 동전 던지기보다 나음 (상대 개선율)"
            insufficient={few(b.all.n)}
          />
          <Metric
            label="기준율만 말한 경우 대비"
            value={b.skill_vs_climatology == null ? '—' : signedRel(b.skill_vs_climatology)}
            sub={b.climatology ? `기준 ${b.climatology.toFixed(3)} · 실제 '예' 비율 ${pct(s.base_rate)}` : "결과에 '예'와 '아니오'가 섞여야 계산"}
            insufficient={few(b.all.n)}
          />
        </div>
        {s.rolling.length >= 3 ? (
          <LineChart points={s.rolling.map((x) => ({ label: shortDate(x.date), value: x.brier, n: x.n }))} min={0} max={0.5} target={0.25} format={(v) => v.toFixed(2)} ariaLabel="브라이어 이동평균" />
        ) : (
          <Empty>결과가 3건 이상 나오면 최근 10건 이동평균 추세를 그립니다.</Empty>
        )}
      </Panel>
      <Panel title="보정 곡선">
        {s.counts.resolved === 0 ? <Empty>결과가 나온 예측이 없습니다.</Empty> : <CalibrationChart bins={s.calibration} ariaLabel="보정 곡선" />}
        <p className="small muted">대각선에 붙을수록 잘 보정된 것. 대각선 아래 = 그 확률대에서 과신. 점 크기 = 예측 수, 세로선 = 95% 구간.</p>
      </Panel>
      <Panel title="과신 진단">
        {oc ? (
          <>
            <div className="metrics-2">
              <Metric label="고른 쪽 확신 평균" value={pct(oc.mean_conf)} sub={`n=${oc.n}`} />
              <Metric label="실제 적중률" value={pct(oc.hit_rate)} sub={`95% 구간 ${pct(oc.lo)}~${pct(oc.hi)}`} />
            </div>
            <p className={`verdict ${oc.verdict}`}>{verdictText(oc, s.min_n)}</p>
          </>
        ) : (
          <Empty>50%가 아닌 예측의 결과가 나오면 계산합니다.</Empty>
        )}
      </Panel>
      <Panel title="머피 분해">
        {s.decomposition ? (
          <table className="table">
            <tbody>
              <tr><td>신뢰도 — 말한 확률과 실제 빈도의 차이 (작을수록 좋음)</td><td className="num">{s.decomposition.reliability.toFixed(3)}</td></tr>
              <tr><td>분해능 — 일어날 일과 안 일어날 일을 가르는 힘 (클수록 좋음)</td><td className="num">{s.decomposition.resolution.toFixed(3)}</td></tr>
              <tr><td>불확실성 — 질문들 자체의 어려움</td><td className="num">{s.decomposition.uncertainty.toFixed(3)}</td></tr>
            </tbody>
          </table>
        ) : (
          <Empty>결과가 나온 예측이 없습니다.</Empty>
        )}
        <p className="small muted">브라이어 = 신뢰도 − 분해능 + 불확실성</p>
      </Panel>
      <Panel title="판단의 질 × 결과 (결정 기록)">
        <div className="quad">
          <span />
          <span className="quad-head">결과 좋음</span>
          <span className="quad-head">결과 나쁨</span>
          <span className="quad-head">판단 좋음</span>
          <div className="cell good"><b>{q.skill}</b>실력</div>
          <div className="cell warn"><b>{q.bad_luck}</b>불운</div>
          <span className="quad-head">판단 나쁨</span>
          <div className="cell bad"><b>{q.dumb_luck}</b>행운 (위험)</div>
          <div className="cell"><b>{q.deserved}</b>당연</div>
        </div>
        <p className="small muted">결과만 보고 판단을 평가하지 않기 위한 표. '행운' 칸이 많으면 위험 신호입니다. 보통(3점) {q.neutral}건은 뺐습니다.</p>
      </Panel>
      <Panel title="분야별 브라이어">
        {s.by_domain.length === 0 ? (
          <Empty>결과가 나온 예측이 없습니다.</Empty>
        ) : (
          <table className="table">
            <thead><tr><th>분야</th><th>브라이어</th><th>n</th></tr></thead>
            <tbody>
              {s.by_domain.map((d) => (
                <tr key={d.label} className={d.n < 5 ? 'dim' : ''}><td>{d.label}</td><td className="num">{fixed(d.brier)}</td><td className="num">{d.n}</td></tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
      <Panel title="자주 나온 편향">
        {s.bias.length === 0 ? (
          <Empty>결과를 입력할 때 돌아본 편향을 고르면 여기 쌓입니다.</Empty>
        ) : (
          <ul className="list">
            {s.bias.map((x) => (
              <li key={x.tag} className="list-row"><span className="grow">{x.label}</span><b>{x.count}</b></li>
            ))}
          </ul>
        )}
      </Panel>
      <Panel title="확률 수정은 도움이 됐나">
        {s.updates_effect.n === 0 ? (
          <Empty>확률을 고친 예측의 결과가 아직 없습니다.</Empty>
        ) : (
          <>
            <div className="metrics-2">
              <Metric label="처음 확률로 채점했다면" value={fixed(s.updates_effect.initial)} />
              <Metric label="고친 확률로 실제 채점" value={fixed(s.updates_effect.final)} sub={`n=${s.updates_effect.n}`} />
            </div>
            <p className="small muted">
              {s.updates_effect.n < 5
                ? `고친 예측이 5건 이상 쌓이면 판단합니다 (지금 ${s.updates_effect.n}건).`
                : (s.updates_effect.final ?? 0) <= (s.updates_effect.initial ?? 0)
                  ? '고친 쪽이 더 좋습니다 — 새 정보를 잘 반영하고 있습니다.'
                  : '고친 쪽이 더 나쁩니다 — 잡음에 흔들렸는지 돌아보세요.'}
            </p>
          </>
        )}
      </Panel>
    </div>
  );
}
